import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PageTitle from '../../components/shared/PageTitle';
import { useAuth } from '../../contexts/AuthContext';
import { useAbility } from '../../contexts/AbilityContext';
import { useTranslation } from 'react-i18next';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { valibotResolver } from '@hookform/resolvers/valibot';
import * as v from 'valibot';
import { useGetVendorMeQuery, useUpdateVendorMutation, useGetRewardSettingsQuery, useUpdateRewardSettingsMutation, useTriggerReviewExtractionMutation, useGetExtractionStatusQuery } from '../../store/vendorApi';
import { User, Mail, Building2, MapPin, Phone, Globe, Save, Eye, FileText, Lightbulb, ListOrdered, Rocket, Loader, Plus, Trash2, Star, CreditCard, Package, Sparkles, Bell, RotateCcw, Shield, Send, Tag, X, Bot } from 'lucide-react';
import { INDUSTRY_SECTORS } from '@clareefai/types';
import { uploadFile } from '../../utils/upload';
import { toast, toastMessages, PhoneInput, CountrySelect, ImageUploadWithCrop } from '@clareefai/ui';
import { getIconHorseUrl } from '@clareefai/utils';
import { useProductTour } from '../../contexts/ProductTourContext';
import PermissionsSettings from './PermissionsSettings';
import PromoterActionsSettings from './PromoterActionsSettings';

const PROVIDER_LABELS: Record<string, string> = {
  GOOGLE_REVIEWS: 'Google Reviews',
  CAPTERRA: 'Capterra',
  G2: 'G2',
  TRUSTPILOT: 'Trustpilot',
};

const ExternalReviewExtractionSection: React.FC = () => {
  const { t } = useTranslation();
  const [triggerExtraction, { isLoading: isExtracting }] = useTriggerReviewExtractionMutation();
  const { data: jobs } = useGetExtractionStatusQuery(undefined, { pollingInterval: 10_000 });

  const latestByProvider = React.useMemo(() => {
    if (!jobs) return {};
    const map: Record<string, (typeof jobs)[number]> = {};
    for (const job of jobs) {
      if (!map[job.provider] || new Date(job.createdAt) > new Date(map[job.provider].createdAt)) {
        map[job.provider] = job;
      }
    }
    return map;
  }, [jobs]);

  const handleExtract = async () => {
    try {
      await triggerExtraction().unwrap();
    } catch {
      // handled by RTK
    }
  };

  const hasAnyJobs = Object.keys(latestByProvider).length > 0;

  return (
    <div className="mt-6 bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center">
          <Star className="w-5 h-5 mr-2 text-yellow-500" />
          {t('settings.reviewExtraction.heading')}
        </h2>
        <button
          onClick={handleExtract}
          disabled={isExtracting}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isExtracting ? (
            <><Loader className="w-4 h-4 animate-spin" /> {t('settings.reviewExtraction.extracting')}</>
          ) : (
            <><Rocket className="w-4 h-4" /> {t('settings.reviewExtraction.extract')}</>
          )}
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        {t('settings.reviewExtraction.description')}
      </p>
      {hasAnyJobs && (
        <div className="space-y-3">
          {Object.entries(latestByProvider).map(([provider, job]) => (
            <div key={provider} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="font-medium text-sm text-gray-900">
                  {PROVIDER_LABELS[provider] || provider}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  job.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                  job.status === 'FAILED' ? 'bg-red-100 text-red-700' :
                  job.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {job.status}
                </span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                {job.averageRating != null && (
                  <span className="flex items-center gap-1 text-gray-700">
                    <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
                    {job.averageRating.toFixed(1)}
                  </span>
                )}
                {job.totalReviews != null && (
                  <span className="text-gray-500">{t('settings.reviewExtraction.reviewsCount', { count: job.totalReviews })}</span>
                )}
                {job.errorMessage && (
                  <span className="text-red-500 text-xs">{job.errorMessage}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface PreferredIndustriesSectionProps {
  value: string[];
  onChange: (next: string[]) => void;
}

const PreferredIndustriesSection: React.FC<PreferredIndustriesSectionProps> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const [customInput, setCustomInput] = useState('');
  const selected = value;
  const isSelected = (label: string) =>
    selected.some(s => s.toLowerCase() === label.toLowerCase());

  const togglePreset = (label: string) => {
    if (isSelected(label)) {
      onChange(selected.filter(s => s.toLowerCase() !== label.toLowerCase()));
    } else {
      onChange([...selected, label]);
    }
  };

  const removeTag = (label: string) => {
    onChange(selected.filter(s => s !== label));
  };

  const addCustom = () => {
    const trimmed = customInput.trim().slice(0, 50);
    if (!trimmed) return;
    if (isSelected(trimmed)) {
      toast.error(t('settings.profile.preferredIndustries.duplicate'));
      return;
    }
    onChange([...selected, trimmed]);
    setCustomInput('');
  };

  return (
    <div className="mt-6 bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
        <Tag className="w-5 h-5 mr-2 text-purple-600" />
        {t('settings.profile.preferredIndustries.heading')}
      </h2>
      <p className="text-sm text-gray-500 mb-4">
        {t('settings.profile.preferredIndustries.description')}
      </p>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.profile.preferredIndustries.presetsLabel')}
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {INDUSTRY_SECTORS.map(sector => {
            const active = isSelected(sector);
            return (
              <button
                key={sector}
                type="button"
                onClick={() => togglePreset(sector)}
                className={`px-3 py-2 text-sm rounded-lg border-2 text-left transition-colors ${
                  active
                    ? 'bg-purple-100 border-purple-400 text-purple-800'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                }`}
              >
                {sector}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.profile.preferredIndustries.customLabel')}
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={customInput}
            onChange={e => setCustomInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addCustom();
              }
            }}
            maxLength={50}
            placeholder={t('settings.profile.preferredIndustries.addPlaceholder')}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            type="button"
            onClick={addCustom}
            disabled={!customInput.trim()}
            className="flex items-center gap-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" />
            {t('settings.profile.preferredIndustries.addButton')}
          </button>
        </div>
      </div>

      {selected.length === 0 ? (
        <p className="text-sm text-gray-500 italic">
          {t('settings.profile.preferredIndustries.empty')}
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {selected.map(tag => (
            <span
              key={tag}
              className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800"
            >
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="ml-2 hover:bg-purple-200 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const productSchema = v.object({
  name: v.pipe(v.string(), v.minLength(1, 'Product name is required'), v.maxLength(100, 'Product name must not exceed 100 characters')),
  description: v.pipe(v.string(), v.minLength(1, 'Product description is required'), v.maxLength(1000, 'Product description must not exceed 1000 characters')),
});

const optionalString = (maxLength?: number, minLength?: number) => {
  if (minLength && maxLength) {
    return v.union([
      v.pipe(v.string(), v.minLength(minLength, `Must be at least ${minLength} characters`), v.maxLength(maxLength, `Must not exceed ${maxLength} characters`)),
      v.literal(''),
    ]);
  } else if (minLength) {
    return v.union([
      v.pipe(v.string(), v.minLength(minLength, `Must be at least ${minLength} characters`)),
      v.literal(''),
    ]);
  } else if (maxLength) {
    return v.union([
      v.pipe(v.string(), v.maxLength(maxLength, `Must not exceed ${maxLength} characters`)),
      v.literal(''),
    ]);
  }
  return v.union([v.string(), v.literal('')]);
};

const LINKEDIN_PERSON_REGEX = /^https:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9_-]+/;

const settingsSchema = v.object({
  firstName: v.pipe(v.string(), v.minLength(2, 'First name must be between 2 and 50 characters'), v.maxLength(50, 'First name must be between 2 and 50 characters')),
  lastName: v.pipe(v.string(), v.minLength(2, 'Last name must be between 2 and 50 characters'), v.maxLength(50, 'Last name must be between 2 and 50 characters')),
  email: v.pipe(v.string(), v.email('Email must be valid')),
  vendorLinkedin: v.union([
    v.pipe(v.string(), v.regex(LINKEDIN_PERSON_REGEX, 'Must be a valid LinkedIn profile URL (e.g. https://linkedin.com/in/username)')),
    v.literal(''),
  ]),
  companyLinkedin: v.union([
    v.pipe(v.string(), v.regex(/^https:\/\/(www\.)?linkedin\.com\/company\/[a-zA-Z0-9_-]+/, 'Must be a valid LinkedIn company URL (e.g. https://linkedin.com/company/companyname)')),
    v.literal(''),
  ]),
  phone: optionalString(20),
  company: optionalString(100, 2),
  businessIdentificationNumber: optionalString(100),
  website: v.pipe(v.string(), v.nonEmpty('Website is required'), v.maxLength(500, 'Website URL must not exceed 500 characters')),
  street: optionalString(200),
  zip: optionalString(20),
  city: optionalString(100),
  country: v.pipe(v.string(), v.nonEmpty('Country is required')),
  about: optionalString(5000),
  googleReviewsUrl: optionalString(500),
  g2ReviewsUrl: optionalString(500),
  products: v.array(productSchema),
  logo: optionalString(500),
  description: optionalString(5000),
  toneOfVoice: optionalString(2000),
  avatar: optionalString(500),
  billingFirstName: optionalString(50, 2),
  billingLastName: optionalString(50, 2),
  billingEmail: optionalString(100),
  billingStreet: optionalString(200),
  billingZip: optionalString(20),
  billingCity: optionalString(100),
  billingCountry: v.pipe(v.string(), v.nonEmpty('Billing country is required')),
  billingPhone: v.pipe(v.string(), v.nonEmpty('Billing phone is required')),
  preferredIndustries: v.array(v.pipe(v.string(), v.maxLength(50))),
});

const settingsSchemaWithBillingCheck = settingsSchema;

type SettingsFormData = v.InferInput<typeof settingsSchema>;

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isTeamMember } = useAuth();
  const ability = useAbility();
  const { restartTour } = useProductTour();
  const { data: vendor, isLoading: isLoadingVendor, error: vendorError } = useGetVendorMeQuery(undefined, { skip: isTeamMember });
  const [updateVendor, { isLoading: isUpdating }] = useUpdateVendorMutation();
  const { data: rewardSettings } = useGetRewardSettingsQuery();
  const [updateRewardSettings] = useUpdateRewardSettingsMutation();
  const allTabsForNav = [
    { id: 'profile', label: t('settings.tabs.profile'), icon: User },
    { id: 'products', label: t('settings.tabs.products'), icon: Package },
    { id: 'invitationAudience', label: t('settings.tabs.invitationAudience'), icon: Tag },
    { id: 'onboarding', label: t('settings.tabs.onboarding'), icon: FileText },
    { id: 'promoterActions', label: t('settings.tabs.promoterActions'), icon: ListOrdered },
    { id: 'notifications', label: t('settings.tabs.notifications'), icon: Bell, isRoute: true },
    ...(ability.can('manage', 'EmailTemplate') ? [{ id: 'emailTemplates', label: t('settings.tabs.emailTemplates'), icon: Mail }] : []),
    { id: 'toneOfVoice', label: t('settings.tabs.toneOfVoice'), icon: Sparkles },
    ...(ability.can('update', 'UseCase') ? [{ id: 'useCaseQuestions', label: t('settings.tabs.useCaseQuestions'), icon: Lightbulb }] : []),
    ...(ability.can('update', 'UseCase') ? [{ id: 'reviewQuestions', label: t('settings.tabs.reviewQuestions'), icon: Star }] : []),
    ...(ability.can('manage', 'Permission') ? [{ id: 'permissions', label: t('settings.tabs.permissions'), icon: Shield }] : []),
    { id: 'emailSender', label: t('settings.tabs.emailSender'), icon: Send, isRoute: true },
    ...(ability.can('manage', 'AgentAccess') ? [{ id: 'agentAccess', label: t('settings.tabs.agentAccess'), icon: Bot, isRoute: true }] : []),
    // Widget configuration moved to the top-level "Widget Management" hub (sidebar).
  ];
  const tabs = isTeamMember
    ? allTabsForNav.filter((t: { id: string }) => !['profile', 'products', 'invitationAudience', 'onboarding', 'promoterActions'].includes(t.id))
    : allTabsForNav;
  const defaultTab = isTeamMember ? (tabs[0]?.id ?? 'profile') : 'profile';
  const [activeTab, setActiveTab] = useState((location.state as { activeTab?: string })?.activeTab || defaultTab);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [localMaxUseCases, setLocalMaxUseCases] = useState<number | null>(null);
  const [localMaxVideos, setLocalMaxVideos] = useState<number | null>(null);
  const [isSavingLimits, setIsSavingLimits] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
    setValue,
  } = useForm<SettingsFormData>({
    resolver: valibotResolver(settingsSchemaWithBillingCheck),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      vendorLinkedin: '',
      companyLinkedin: '',
      phone: '',
      company: '',
      businessIdentificationNumber: '',
      website: '',
      street: '',
      zip: '',
      city: '',
      country: '',
      about: '',
      googleReviewsUrl: '',
      g2ReviewsUrl: '',
      products: [],
      logo: '',
      description: '',
      avatar: '',
      billingFirstName: '',
      billingLastName: '',
      billingEmail: '',
      billingStreet: '',
      billingZip: '',
      billingCity: '',
      billingCountry: '',
      billingPhone: '',
      preferredIndustries: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'products',
  });

  const [notifications, setNotifications] = useState({
    emailNewPromoter: true,
    emailNewProspect: true,
    emailNewMeeting: true,
    emailNewReview: true,
    emailNewTestimonial: true,
    emailWeeklyReport: true,
    emailMonthlyReport: false,
    sendAutomaticReminders: true,
  });

  const [privacy, setPrivacy] = useState({
    profileVisibility: 'public',
    showEmail: false,
    showPhone: false,
    allowAnalytics: true,
  });

  const [onboardingData, setOnboardingData] = useState<any>(null);
  const originalDataRef = useRef<SettingsFormData | null>(null);

  useEffect(() => {
    const data = localStorage.getItem('onboardingData');
    if (data) {
      setOnboardingData(JSON.parse(data));
    }
  }, []);

  // Watch all form values for change detection
  const formValues = watch();

  useEffect(() => {
    if (vendor) {
      let street = vendor.streetAddress || '';
      let zip = vendor.zipCode || '';
      let city = vendor.city || '';
      let country = vendor.country || '';

      if (vendor.address && !street && !zip && !city && !country) {
        const delimiter = vendor.address.includes(' | ') ? ' | ' : ',';
        const parts = vendor.address.split(delimiter).map(p => p.trim());
        if (parts.length >= 1) street = parts[0];
        if (parts.length >= 2) zip = parts[1];
        if (parts.length >= 3) city = parts[2];
        if (parts.length >= 4) country = parts[3];
      }

      let billingStreet = vendor.billingContact?.streetAddress || '';
      let billingZip = vendor.billingContact?.zipCode || '';
      let billingCity = vendor.billingContact?.city || '';
      let billingCountry = vendor.billingContact?.country || '';

      if (vendor.billingContact?.address && !billingStreet && !billingZip && !billingCity && !billingCountry) {
        const billingDelimiter = vendor.billingContact.address.includes(' | ') ? ' | ' : ',';
        const billingParts = vendor.billingContact.address.split(billingDelimiter).map(p => p.trim());
        if (billingParts.length >= 1) billingStreet = billingParts[0];
        if (billingParts.length >= 2) billingZip = billingParts[1];
        if (billingParts.length >= 3) billingCity = billingParts[2];
        if (billingParts.length >= 4) billingCountry = billingParts[3];
      }

      const initialData: SettingsFormData = {
        firstName: vendor.firstName || '',
        lastName: vendor.lastName || '',
        email: vendor.email || '',
        vendorLinkedin: vendor.vendorLinkedin || '',
        companyLinkedin: vendor.companyLinkedin || '',
        phone: vendor.phone || vendor.billingContact?.phone || '',
        company: vendor.companyName || '',
        businessIdentificationNumber: (vendor as { businessIdentificationNumber?: string }).businessIdentificationNumber || '',
        website: vendor.pageInfo?.companyUrl || '',
        street,
        zip,
        city,
        country,
        about: vendor.pageInfo?.about || '',
        googleReviewsUrl: vendor.pageInfo?.googleReviewsUrl || '',
        g2ReviewsUrl: vendor.pageInfo?.g2ReviewsUrl || '',
        products: vendor.pageInfo?.products || [],
        logo: vendor.logo || '',
        description: vendor.description || '',
        avatar: vendor.avatar || '',
        toneOfVoice: (vendor as { toneOfVoice?: string }).toneOfVoice || '',
        billingFirstName: vendor.billingContact?.firstName || '',
        billingLastName: vendor.billingContact?.lastName || '',
        billingEmail: vendor.billingContact?.email || '',
        billingStreet,
        billingZip,
        billingCity,
        billingCountry,
        billingPhone: vendor.billingContact?.phone || '',
        preferredIndustries: vendor.preferredIndustries ?? [],
      };
      reset(initialData);
      originalDataRef.current = initialData;
    }
  }, [vendor, reset]);

  const hasChanges = useMemo(() => {
    if (!originalDataRef.current) return false;
    const original = originalDataRef.current;
    const current = formValues;

    return (
      current.firstName !== original.firstName ||
      current.lastName !== original.lastName ||
      current.vendorLinkedin !== original.vendorLinkedin ||
      current.companyLinkedin !== original.companyLinkedin ||
      current.phone !== original.phone ||
      current.company !== original.company ||
      current.businessIdentificationNumber !== original.businessIdentificationNumber ||
      current.website !== original.website ||
      current.street !== original.street ||
      current.zip !== original.zip ||
      current.city !== original.city ||
      current.country !== original.country ||
      current.about !== original.about ||
      current.googleReviewsUrl !== original.googleReviewsUrl ||
      current.g2ReviewsUrl !== original.g2ReviewsUrl ||
      current.logo !== original.logo ||
      current.description !== original.description ||
      current.avatar !== original.avatar ||
      current.toneOfVoice !== original.toneOfVoice ||
      current.billingFirstName !== original.billingFirstName ||
      current.billingLastName !== original.billingLastName ||
      current.billingEmail !== original.billingEmail ||
      current.billingStreet !== original.billingStreet ||
      current.billingZip !== original.billingZip ||
      current.billingCity !== original.billingCity ||
      current.billingCountry !== original.billingCountry ||
      current.billingPhone !== original.billingPhone ||
      JSON.stringify([...(current.preferredIndustries ?? [])].sort()) !==
      JSON.stringify([...(original.preferredIndustries ?? [])].sort()) ||
      JSON.stringify(current.products?.map(p => ({ name: p.name, description: p.description })).sort((a, b) => a.name.localeCompare(b.name))) !==
      JSON.stringify(original.products?.map(p => ({ name: p.name, description: p.description })).sort((a, b) => a.name.localeCompare(b.name)))
    );
  }, [formValues]);

  const isFormValid = useMemo(() => {
    const current = formValues;

    if (current.vendorLinkedin && current.vendorLinkedin.trim() !== '') {
      try {
        const url = new URL(current.vendorLinkedin);
        if (url.protocol !== 'https:') return false;
      } catch {
        return false;
      }
    }

    if (current.companyLinkedin && current.companyLinkedin.trim() !== '') {
      try {
        const url = new URL(current.companyLinkedin);
        if (url.protocol !== 'https:') return false;
      } catch {
        return false;
      }
    }

    if (current.website && current.website.trim() !== '') {
      try {
        const url = new URL(current.website);
        if (url.protocol !== 'https:' && url.protocol !== 'http:') return false;
      } catch {
        return false;
      }
    }

    if (current.googleReviewsUrl && current.googleReviewsUrl.trim() !== '') {
      try {
        const url = new URL(current.googleReviewsUrl);
        if (url.protocol !== 'https:' && url.protocol !== 'http:') return false;
      } catch {
        return false;
      }
    }

    if (current.g2ReviewsUrl && current.g2ReviewsUrl.trim() !== '') {
      try {
        const url = new URL(current.g2ReviewsUrl);
        if (url.protocol !== 'https:' && url.protocol !== 'http:') return false;
      } catch {
        return false;
      }
    }

    return true;
  }, [formValues]);

  useEffect(() => {
    const stateTab = (location.state as { activeTab?: string })?.activeTab;
    if (stateTab) setActiveTab(stateTab);
  }, [location.state]);

  const handleNotificationChange = (key: string) => {
    setNotifications(prev => ({ ...prev, [key]: !prev[key as keyof typeof prev] }));
  };

  const handlePrivacyChange = (key: string, value: any) => {
    setPrivacy(prev => ({ ...prev, [key]: value }));
  };

  const onInvalidSubmit = () => {
    toast.error(t('settings.profile.fixErrors'));
    setTimeout(() => {
      const firstError = document.querySelector<HTMLElement>('.border-red-500');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstError.focus({ preventScroll: true });
      }
    }, 50);
  };

  const onSubmit = async (data: SettingsFormData) => {
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const pageInfoData: any = {};
      if (data.website) pageInfoData.companyUrl = data.website;
      if (data.about) pageInfoData.about = data.about;
      if (data.googleReviewsUrl) pageInfoData.googleReviewsUrl = data.googleReviewsUrl;
      if (data.g2ReviewsUrl) pageInfoData.g2ReviewsUrl = data.g2ReviewsUrl;
      pageInfoData.products = data.products;

      const updatePayload: any = {};
      updatePayload.email = vendor?.email ?? data.email.trim();
      if (data.firstName.trim()) updatePayload.firstName = data.firstName.trim();
      if (data.lastName.trim()) updatePayload.lastName = data.lastName.trim();
      if (data.company?.trim()) updatePayload.companyName = data.company.trim();
      if (data.businessIdentificationNumber?.trim()) updatePayload.businessIdentificationNumber = data.businessIdentificationNumber.trim();
      if (data.logo?.trim()) updatePayload.logo = data.logo.trim();
      if (data.vendorLinkedin?.trim()) updatePayload.vendorLinkedin = data.vendorLinkedin.trim();
      if (data.companyLinkedin?.trim()) updatePayload.companyLinkedin = data.companyLinkedin.trim();
      if (data.description?.trim()) updatePayload.description = data.description.trim();
      if (data.avatar?.trim()) updatePayload.avatar = data.avatar.trim();
      if (data.phone?.trim()) updatePayload.phone = data.phone.trim();
      if ((data as { toneOfVoice?: string }).toneOfVoice !== undefined) (updatePayload as Record<string, unknown>).toneOfVoice = (data as { toneOfVoice?: string }).toneOfVoice?.trim() || null;

      if (data.street?.trim()) updatePayload.streetAddress = data.street.trim();
      if (data.zip?.trim()) updatePayload.zipCode = data.zip.trim();
      if (data.city?.trim()) updatePayload.city = data.city.trim();
      if (data.country?.trim()) updatePayload.country = data.country.trim();
      const addressParts = [data.street, data.zip, data.city, data.country].filter(Boolean);
      if (addressParts.length > 0) {
        updatePayload.address = addressParts.join(', ');
      }

      if (Object.keys(pageInfoData).length > 0) updatePayload.pageInfo = pageInfoData;

      updatePayload.preferredIndustries = data.preferredIndustries ?? [];

      const hasAnyBilling = [data.billingFirstName, data.billingLastName, data.billingEmail, data.billingStreet, data.billingZip, data.billingCity].some(Boolean);
      const hasBilling = hasAnyBilling && data.billingPhone?.trim() && data.billingCountry?.trim();
      if (hasBilling) {
        const billingAddressParts = [data.billingStreet, data.billingZip, data.billingCity, data.billingCountry].filter(Boolean);
        updatePayload.billingContact = {
          firstName: data.billingFirstName?.trim() || vendor?.billingContact?.firstName || '',
          lastName: data.billingLastName?.trim() || vendor?.billingContact?.lastName || '',
          email: data.billingEmail?.trim() || vendor?.billingContact?.email || vendor?.email || '',
          phone: data.billingPhone?.trim() || '',
          address: billingAddressParts.length > 0 ? billingAddressParts.join(', ') : (data.billingCountry?.trim() ?? ''),
          streetAddress: data.billingStreet?.trim() || '',
          zipCode: data.billingZip?.trim() || '',
          city: data.billingCity?.trim() || '',
          country: data.billingCountry?.trim() || '',
        };
      }

      await updateVendor(updatePayload).unwrap();
      toast.success(toastMessages.vendor.updated);
      setSuccessMessage(t('settings.profile.updateSuccess'));
    } catch (err: any) {
      const errorMsg = err?.data?.message || t('settings.profile.updateError');
      toast.error(errorMsg);
      setErrorMessage(errorMsg);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <PageTitle title={t('settings.pageTitle')} />
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('settings.pageTitle')}</h1>
          <p className="text-gray-600">{t('settings.pageDescription')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-md p-4">
              <nav className="space-y-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      if (tab.id === 'useCaseQuestions') {
                        navigate('/settings/use-case-questions');
                        return;
                      }
                      if (tab.id === 'reviewQuestions') {
                        navigate('/settings/review-questions');
                        return;
                      }
                      if ((tab as { isRoute?: boolean }).isRoute && tab.id === 'notifications') {
                        navigate('/settings/notifications');
                        return;
                      }
                      if ((tab as { isRoute?: boolean }).isRoute && tab.id === 'emailSender') {
                        navigate('/settings/email-integration');
                        return;
                      }
                      if ((tab as { isRoute?: boolean }).isRoute && tab.id === 'agentAccess') {
                        navigate('/settings/agent-access');
                        return;
                      }
                      setActiveTab(tab.id);
                    }}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-50 text-blue-700 font-semibold'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <tab.icon className="w-5 h-5" />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl shadow-md p-6">
              {activeTab === 'profile' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('settings.profile.heading')}</h2>

                  {isLoadingVendor && (
                    <div className="flex items-center justify-center py-12">
                      <Loader className="w-8 h-8 animate-spin text-purple-600" />
                    </div>
                  )}

                  {vendorError && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                      {t('settings.profile.loadError')}
                    </div>
                  )}

                  {successMessage && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                      {successMessage}
                    </div>
                  )}

                  {errorMessage && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                      {errorMessage}
                    </div>
                  )}

                  {!isLoadingVendor && (
                    <form onSubmit={handleSubmit(onSubmit, onInvalidSubmit)}>
                  {/* Avatar upload removed — initials used in vendor app */}
                  {/* <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Vendor Avatar</label>
                      <Controller name="avatar" control={control} render={({ field }) => (
                        <ImageUploadWithCrop value={field.value} onChange={field.onChange}
                          onUpload={(blob) => uploadFile(blob, 'avatars')} aspect={1}
                          placeholder="Upload vendor avatar" disabled={isUpdating} error={Boolean(errors.avatar)} />
                      )} />
                    </div>
                  </div> */}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <User className="w-4 h-4 inline mr-1" />
                        {t('settings.profile.firstName')}
                      </label>
                      <input
                        type="text"
                        {...register('firstName')}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.firstName ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.firstName && (
                        <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <User className="w-4 h-4 inline mr-1" />
                        {t('settings.profile.lastName')}
                      </label>
                      <input
                        type="text"
                        {...register('lastName')}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.lastName ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.lastName && (
                        <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <Mail className="w-4 h-4 inline mr-1" />
                        {t('settings.profile.email')}
                      </label>
                      <input
                        type="email"
                        {...register('email')}
                        readOnly
                        className={`w-full px-4 py-3 border rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed ${
                          errors.email ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('settings.profile.vendorLinkedin')}
                      </label>
                      <input
                        type="url"
                        {...register('vendorLinkedin')}
                        placeholder={t('settings.profile.vendorLinkedinPlaceholder')}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.vendorLinkedin ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.vendorLinkedin && (
                        <p className="mt-1 text-sm text-red-600">{errors.vendorLinkedin.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <Phone className="w-4 h-4 inline mr-1" />
                        {t('settings.profile.phone')}
                      </label>
                      <Controller
                        name="phone"
                        control={control}
                        render={({ field }) => (
                          <PhoneInput
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            error={Boolean(errors.phone)}
                          />
                        )}
                      />
                      {errors.phone && (
                        <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <Building2 className="w-4 h-4 inline mr-1" />
                        {t('settings.profile.company')}
                      </label>
                      <input
                        type="text"
                        {...register('company')}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.company ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.company && (
                        <p className="mt-1 text-sm text-red-600">{errors.company.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('settings.profile.businessId')}
                      </label>
                      <input
                        type="text"
                        {...register('businessIdentificationNumber')}
                        placeholder={t('settings.profile.businessIdPlaceholder')}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.businessIdentificationNumber ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.businessIdentificationNumber && (
                        <p className="mt-1 text-sm text-red-600">{errors.businessIdentificationNumber.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('settings.profile.companyLinkedin')}
                      </label>
                      <input
                        type="url"
                        {...register('companyLinkedin')}
                        placeholder={t('settings.profile.companyLinkedinPlaceholder')}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.companyLinkedin ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.companyLinkedin && (
                        <p className="mt-1 text-sm text-red-600">{errors.companyLinkedin.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <Globe className="w-4 h-4 inline mr-1" />
                        {t('settings.profile.website')} <span className="text-red-500">*</span>
                      </label>
                      <Controller
                        name="website"
                        control={control}
                        render={({ field }) => (
                          <input
                            {...field}
                            type="url"
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                              errors.website ? 'border-red-500' : 'border-gray-300'
                            }`}
                            onBlur={(e) => {
                              field.onBlur(e);
                              const url = e.target.value?.trim();
                              if (url && !watch('logo')?.trim()) {
                                const iconUrl = getIconHorseUrl(url);
                                if (iconUrl) setValue('logo', iconUrl);
                              }
                            }}
                          />
                        )}
                      />
                      {errors.website && (
                        <p className="mt-1 text-sm text-red-600">{errors.website.message}</p>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <MapPin className="w-4 h-4 inline mr-1" />
                        {t('settings.profile.streetAddress')}
                      </label>
                      <input
                        type="text"
                        {...register('street')}
                        placeholder={t('settings.profile.streetAddressPlaceholder')}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.street ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.street && (
                        <p className="mt-1 text-sm text-red-600">{errors.street.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.profile.zipCode')}</label>
                      <input
                        type="text"
                        {...register('zip')}
                        placeholder={t('settings.profile.zipPlaceholder')}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.zip ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.zip && (
                        <p className="mt-1 text-sm text-red-600">{errors.zip.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.profile.city')}</label>
                      <input
                        type="text"
                        {...register('city')}
                        placeholder={t('settings.profile.cityPlaceholder')}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.city ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.city && (
                        <p className="mt-1 text-sm text-red-600">{errors.city.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.profile.country')} <span className="text-red-500">*</span></label>
                      <Controller
                        name="country"
                        control={control}
                        render={({ field }) => (
                          <CountrySelect
                            value={field.value}
                            onChange={field.onChange}
                            placeholder={t('settings.profile.countryPlaceholder')}
                            error={Boolean(errors.country)}
                          />
                        )}
                      />
                      {errors.country && (
                        <p className="mt-1 text-sm text-red-600">{errors.country.message}</p>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.profile.description')}</label>
                      <textarea
                        {...register('description')}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[100px] ${
                          errors.description ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder={t('settings.profile.descriptionPlaceholder')}
                        rows={4}
                      />
                      {errors.description && (
                        <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.profile.companyLogo')}</label>
                      <Controller
                        name="logo"
                        control={control}
                        render={({ field }) => (
                          <ImageUploadWithCrop
                            value={field.value}
                            onChange={field.onChange}
                            onUpload={(blob) => uploadFile(blob, 'logos')}
                            aspect={16 / 9}
                            placeholder={t('settings.profile.uploadLogo')}
                            disabled={isUpdating}
                            error={Boolean(errors.logo)}
                            allowUrlInput
                          />
                        )}
                      />
                      {errors.logo && (
                        <p className="mt-1 text-sm text-red-600">{errors.logo.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="mt-6 bg-white rounded-lg shadow p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Globe className="w-5 h-5 mr-2 text-purple-600" />
                      {t('settings.profile.pageInfo')}
                    </h2>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('settings.profile.aboutSection')}
                        </label>
                        <textarea
                          {...register('about')}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[120px] ${
                            errors.about ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder={t('settings.profile.aboutPlaceholder')}
                        />
                        {errors.about && (
                          <p className="mt-1 text-sm text-red-600">{errors.about.message}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('settings.profile.companyUrl')}
                        </label>
                        <input
                          type="url"
                          {...register('website')}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            errors.website ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder={t('settings.profile.companyUrlPlaceholder')}
                        />
                        {errors.website && (
                          <p className="mt-1 text-sm text-red-600">{errors.website.message}</p>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                            <Star className="w-4 h-4 mr-1" />
                            {t('settings.profile.googleReviewsUrl')}
                          </label>
                          <input
                            type="url"
                            {...register('googleReviewsUrl')}
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                              errors.googleReviewsUrl ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder={t('settings.profile.googleReviewsPlaceholder')}
                          />
                          {errors.googleReviewsUrl && (
                            <p className="mt-1 text-sm text-red-600">{errors.googleReviewsUrl.message}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                            <Star className="w-4 h-4 mr-1" />
                            {t('settings.profile.g2ReviewsUrl')}
                          </label>
                          <input
                            type="url"
                            {...register('g2ReviewsUrl')}
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                              errors.g2ReviewsUrl ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder={t('settings.profile.g2ReviewsPlaceholder')}
                          />
                          {errors.g2ReviewsUrl && (
                            <p className="mt-1 text-sm text-red-600">{errors.g2ReviewsUrl.message}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* External Review Extraction */}
                  <ExternalReviewExtractionSection />

                  <div className="mt-6 bg-white rounded-lg shadow p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <CreditCard className="w-5 h-5 mr-2 text-purple-600" />
                      {t('settings.profile.billingContact')}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.profile.firstName')}</label>
                        <input
                          type="text"
                          {...register('billingFirstName')}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            errors.billingFirstName ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {errors.billingFirstName && (
                          <p className="mt-1 text-sm text-red-600">{errors.billingFirstName.message}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.profile.lastName')}</label>
                        <input
                          type="text"
                          {...register('billingLastName')}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            errors.billingLastName ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {errors.billingLastName && (
                          <p className="mt-1 text-sm text-red-600">{errors.billingLastName.message}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.profile.email')}</label>
                        <input
                          type="email"
                          {...register('billingEmail')}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            errors.billingEmail ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {errors.billingEmail && (
                          <p className="mt-1 text-sm text-red-600">{errors.billingEmail.message}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.profile.phone')} <span className="text-red-500">*</span></label>
                        <Controller
                          name="billingPhone"
                          control={control}
                          render={({ field }) => (
                            <PhoneInput
                              value={field.value}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              error={Boolean(errors.billingPhone)}
                            />
                          )}
                        />
                        {errors.billingPhone && (
                          <p className="mt-1 text-sm text-red-600">{errors.billingPhone.message}</p>
                        )}
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.profile.streetAddress')}</label>
                        <input
                          type="text"
                          {...register('billingStreet')}
                          placeholder={t('settings.profile.streetAddressPlaceholder')}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            errors.billingStreet ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {errors.billingStreet && (
                          <p className="mt-1 text-sm text-red-600">{errors.billingStreet.message}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.profile.zipCode')}</label>
                        <input
                          type="text"
                          {...register('billingZip')}
                          placeholder={t('settings.profile.zipPlaceholder')}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            errors.billingZip ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {errors.billingZip && (
                          <p className="mt-1 text-sm text-red-600">{errors.billingZip.message}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.profile.city')}</label>
                        <input
                          type="text"
                          {...register('billingCity')}
                          placeholder={t('settings.profile.cityPlaceholder')}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            errors.billingCity ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {errors.billingCity && (
                          <p className="mt-1 text-sm text-red-600">{errors.billingCity.message}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.profile.country')} <span className="text-red-500">*</span></label>
                        <Controller
                          name="billingCountry"
                          control={control}
                          render={({ field }) => (
                            <CountrySelect
                              value={field.value}
                              onChange={field.onChange}
                              placeholder={t('settings.profile.countryPlaceholder')}
                              error={Boolean(errors.billingCountry)}
                            />
                          )}
                        />
                        {errors.billingCountry && (
                          <p className="mt-1 text-sm text-red-600">{errors.billingCountry.message}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end">
                    <button
                      type="submit"
                      disabled={!hasChanges || !isFormValid || isUpdating}
                      className="flex items-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isUpdating ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          <span>{t('common.saving')}</span>
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          <span>{t('common.saveChanges')}</span>
                        </>
                      )}
                    </button>
                  </div>
                    </form>
                  )}
                </div>
              )}

              {activeTab === 'invitationAudience' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('settings.invitationAudience.heading')}</h2>
                  <p className="text-gray-600 mb-6">{t('settings.invitationAudience.description')}</p>

                  {isLoadingVendor && (
                    <div className="flex items-center justify-center py-12">
                      <Loader className="w-8 h-8 animate-spin text-purple-600" />
                    </div>
                  )}

                  {!isLoadingVendor && (
                    <form onSubmit={handleSubmit(onSubmit, onInvalidSubmit)}>
                      <Controller
                        name="preferredIndustries"
                        control={control}
                        render={({ field }) => (
                          <PreferredIndustriesSection
                            value={field.value ?? []}
                            onChange={field.onChange}
                          />
                        )}
                      />

                      <div className="mt-6 flex justify-end">
                        <button
                          type="submit"
                          disabled={!hasChanges || !isFormValid || isUpdating}
                          className="flex items-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isUpdating ? (
                            <>
                              <Loader className="w-4 h-4 animate-spin" />
                              <span>{t('common.saving')}</span>
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4" />
                              <span>{t('common.saveChanges')}</span>
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}

              {activeTab === 'products' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('settings.products.heading')}</h2>

                  {isLoadingVendor && (
                    <div className="flex items-center justify-center py-12">
                      <Loader className="w-8 h-8 animate-spin text-purple-600" />
                    </div>
                  )}

                  {!isLoadingVendor && (
                    <form onSubmit={handleSubmit(onSubmit, onInvalidSubmit)}>
                      <div className="flex items-center justify-between mb-6">
                        <p className="text-gray-600">{t('settings.products.intro')}</p>
                        <button
                          type="button"
                          onClick={() => append({ name: '', description: '' })}
                          className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          <span>{t('settings.products.addProduct')}</span>
                        </button>
                      </div>

                      <div className="space-y-4">
                        {fields.length === 0 ? (
                          <p className="text-gray-500 text-sm">{t('settings.products.empty')}</p>
                        ) : (
                          fields.map((field, index) => (
                            <div key={field.id} className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
                              <div className="flex justify-between items-start mb-3">
                                <h3 className="text-sm font-medium text-gray-700">{t('settings.products.productNumber', { number: index + 1 })}</h3>
                                <button
                                  type="button"
                                  onClick={() => remove(index)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="space-y-3">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('settings.products.productName')}
                                  </label>
                                  <input
                                    type="text"
                                    {...register(`products.${index}.name`)}
                                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                      errors.products?.[index]?.name ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    placeholder={t('settings.products.productNamePlaceholder')}
                                  />
                                  {errors.products?.[index]?.name && (
                                    <p className="mt-1 text-sm text-red-600">{errors.products[index]?.name?.message}</p>
                                  )}
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('settings.products.productDescription')}
                                  </label>
                                  <textarea
                                    {...register(`products.${index}.description`)}
                                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[80px] ${
                                      errors.products?.[index]?.description ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    placeholder={t('settings.products.productDescriptionPlaceholder')}
                                  />
                                  {errors.products?.[index]?.description && (
                                    <p className="mt-1 text-sm text-red-600">{errors.products[index]?.description?.message}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      <div className="mt-6 flex justify-end">
                        <button
                          type="submit"
                          disabled={!hasChanges || !isFormValid || isUpdating}
                          className="flex items-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isUpdating ? (
                            <>
                              <Loader className="w-4 h-4 animate-spin" />
                              <span>{t('common.saving')}</span>
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4" />
                              <span>{t('common.saveChanges')}</span>
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}

              {activeTab === 'notifications' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('settings.notifications.heading')}</h2>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-semibold text-gray-900">{t('settings.notifications.newPromoterTitle')}</p>
                        <p className="text-sm text-gray-600">{t('settings.notifications.newPromoterDescription')}</p>
                      </div>
                      <button
                        onClick={() => handleNotificationChange('emailNewPromoter')}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          notifications.emailNewPromoter ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                      >
                        <div
                          className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            notifications.emailNewPromoter ? 'transform translate-x-6' : ''
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-semibold text-gray-900">{t('settings.notifications.newProspectTitle')}</p>
                        <p className="text-sm text-gray-600">{t('settings.notifications.newProspectDescription')}</p>
                      </div>
                      <button
                        onClick={() => handleNotificationChange('emailNewProspect')}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          notifications.emailNewProspect ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                      >
                        <div
                          className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            notifications.emailNewProspect ? 'transform translate-x-6' : ''
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-semibold text-gray-900">{t('settings.notifications.newMeetingTitle')}</p>
                        <p className="text-sm text-gray-600">{t('settings.notifications.newMeetingDescription')}</p>
                      </div>
                      <button
                        onClick={() => handleNotificationChange('emailNewMeeting')}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          notifications.emailNewMeeting ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                      >
                        <div
                          className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            notifications.emailNewMeeting ? 'transform translate-x-6' : ''
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-semibold text-gray-900">{t('settings.notifications.newReviewTitle')}</p>
                        <p className="text-sm text-gray-600">{t('settings.notifications.newReviewDescription')}</p>
                      </div>
                      <button
                        onClick={() => handleNotificationChange('emailNewReview')}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          notifications.emailNewReview ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                      >
                        <div
                          className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            notifications.emailNewReview ? 'transform translate-x-6' : ''
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-semibold text-gray-900">{t('settings.notifications.newTestimonialTitle')}</p>
                        <p className="text-sm text-gray-600">{t('settings.notifications.newTestimonialDescription')}</p>
                      </div>
                      <button
                        onClick={() => handleNotificationChange('emailNewTestimonial')}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          notifications.emailNewTestimonial ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                      >
                        <div
                          className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            notifications.emailNewTestimonial ? 'transform translate-x-6' : ''
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-semibold text-gray-900">{t('settings.notifications.weeklyReportTitle')}</p>
                        <p className="text-sm text-gray-600">{t('settings.notifications.weeklyReportDescription')}</p>
                      </div>
                      <button
                        onClick={() => handleNotificationChange('emailWeeklyReport')}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          notifications.emailWeeklyReport ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                      >
                        <div
                          className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            notifications.emailWeeklyReport ? 'transform translate-x-6' : ''
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-semibold text-gray-900">{t('settings.notifications.monthlyReportTitle')}</p>
                        <p className="text-sm text-gray-600">{t('settings.notifications.monthlyReportDescription')}</p>
                      </div>
                      <button
                        onClick={() => handleNotificationChange('emailMonthlyReport')}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          notifications.emailMonthlyReport ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                      >
                        <div
                          className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            notifications.emailMonthlyReport ? 'transform translate-x-6' : ''
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-semibold text-gray-900">{t('settings.notifications.autoRemindersTitle')}</p>
                        <p className="text-sm text-gray-600">{t('settings.notifications.autoRemindersDescription')}</p>
                      </div>
                      <button
                        onClick={() => handleNotificationChange('sendAutomaticReminders')}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          notifications.sendAutomaticReminders ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                      >
                        <div
                          className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            notifications.sendAutomaticReminders ? 'transform translate-x-6' : ''
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={() => {
                        setSuccessMessage(t('settings.notifications.preferencesSaved'));
                        setTimeout(() => setSuccessMessage(''), 3000);
                      }}
                      className="flex items-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
                    >
                      <Save className="w-4 h-4" />
                      <span>{t('settings.notifications.savePreferences')}</span>
                    </button>
                  </div>
                </div>
              )}

              {activeTab === 'privacy' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('settings.privacy.heading')}</h2>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        <Eye className="w-4 h-4 inline mr-1" />
                        {t('settings.privacy.profileVisibility')}
                      </label>
                      <select
                        value={privacy.profileVisibility}
                        onChange={(e) => handlePrivacyChange('profileVisibility', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="public">{t('settings.privacy.optionPublic')}</option>
                        <option value="private">{t('settings.privacy.optionPrivate')}</option>
                        <option value="team">{t('settings.privacy.optionTeam')}</option>
                      </select>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-semibold text-gray-900">{t('settings.privacy.showEmailTitle')}</p>
                        <p className="text-sm text-gray-600">{t('settings.privacy.showEmailDescription')}</p>
                      </div>
                      <button
                        onClick={() => handlePrivacyChange('showEmail', !privacy.showEmail)}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          privacy.showEmail ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                      >
                        <div
                          className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            privacy.showEmail ? 'transform translate-x-6' : ''
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-semibold text-gray-900">{t('settings.privacy.showPhoneTitle')}</p>
                        <p className="text-sm text-gray-600">{t('settings.privacy.showPhoneDescription')}</p>
                      </div>
                      <button
                        onClick={() => handlePrivacyChange('showPhone', !privacy.showPhone)}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          privacy.showPhone ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                      >
                        <div
                          className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            privacy.showPhone ? 'transform translate-x-6' : ''
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-semibold text-gray-900">{t('settings.privacy.allowAnalyticsTitle')}</p>
                        <p className="text-sm text-gray-600">{t('settings.privacy.allowAnalyticsDescription')}</p>
                      </div>
                      <button
                        onClick={() => handlePrivacyChange('allowAnalytics', !privacy.allowAnalytics)}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          privacy.allowAnalytics ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                      >
                        <div
                          className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            privacy.allowAnalytics ? 'transform translate-x-6' : ''
                          }`}
                        />
                      </button>
                    </div>

                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <h3 className="font-semibold text-red-900 mb-2">{t('settings.privacy.dangerZone')}</h3>
                      <p className="text-sm text-red-700 mb-4">{t('settings.privacy.dangerZoneDescription')}</p>
                      <button className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors">
                        {t('settings.privacy.deleteAccount')}
                      </button>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={() => {
                        setSuccessMessage(t('settings.privacy.saved'));
                        setTimeout(() => setSuccessMessage(''), 3000);
                      }}
                      className="flex items-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
                    >
                      <Save className="w-4 h-4" />
                      <span>{t('settings.privacy.saveSettings')}</span>
                    </button>
                  </div>
                </div>
              )}

              {activeTab === 'onboarding' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">{t('settings.onboarding.heading')}</h2>
                    <button
                      onClick={() => navigate('/onboarding')}
                      className="flex items-center space-x-2 px-4 py-2 bg-[#4339A0] hover:bg-[#362d80] text-white rounded-lg font-semibold transition-colors"
                    >
                      <Rocket className="w-4 h-4" />
                      <span>{t('settings.onboarding.start')}</span>
                    </button>
                  </div>

                  {/* Promoter Content Limits */}
                  <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('settings.onboarding.limitsTitle')}</h3>
                    <p className="text-xs text-gray-500 mb-4">{t('settings.onboarding.limitsDescription')}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.onboarding.maxUseCases')}</label>
                        <input
                          type="number"
                          min={1}
                          max={50}
                          value={localMaxUseCases ?? rewardSettings?.reward_configs.find(c => c.type === 'USE_CASE')?.max_times ?? 1}
                          onChange={(e) => setLocalMaxUseCases(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#4339A0] focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.onboarding.maxVideos')}</label>
                        <input
                          type="number"
                          min={1}
                          max={50}
                          value={localMaxVideos ?? rewardSettings?.reward_configs.find(c => c.type === 'VIDEO')?.max_times ?? 1}
                          onChange={(e) => setLocalMaxVideos(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#4339A0] focus:border-transparent"
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={isSavingLimits || (localMaxUseCases === null && localMaxVideos === null)}
                      onClick={async () => {
                        if (!rewardSettings) return;
                        setIsSavingLimits(true);
                        try {
                          const updatedConfigs = rewardSettings.reward_configs.map(c => {
                            if (c.type === 'USE_CASE' && localMaxUseCases !== null) return { ...c, max_times: localMaxUseCases };
                            if (c.type === 'VIDEO' && localMaxVideos !== null) return { ...c, max_times: localMaxVideos };
                            return c;
                          });
                          await updateRewardSettings({ currency: rewardSettings.currency, reward_configs: updatedConfigs }).unwrap();
                          toast.success(t('settings.onboarding.limitsUpdated'));
                          setLocalMaxUseCases(null);
                          setLocalMaxVideos(null);
                        } catch {
                          toast.error(t('settings.onboarding.limitsError'));
                        } finally {
                          setIsSavingLimits(false);
                        }
                      }}
                      className="flex items-center space-x-2 px-4 py-2 bg-[#4339A0] hover:bg-[#362d80] text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save className="w-4 h-4" />
                      <span>{isSavingLimits ? t('common.saving') : t('settings.onboarding.updateLimits')}</span>
                    </button>
                  </div>

                  {/* Restart Product Tour */}
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-purple-800">{t('settings.onboarding.tourTitle')}</p>
                        <p className="text-xs text-purple-600 mt-0.5">{t('settings.onboarding.tourDescription')}</p>
                      </div>
                      <button
                        onClick={() => {
                          restartTour();
                          navigate('/dashboard');
                        }}
                        className="flex items-center space-x-2 px-3 py-1.5 bg-[#42389D] hover:bg-[#362d80] text-white rounded-lg text-sm font-medium transition-colors"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        <span>{t('settings.onboarding.restartTour')}</span>
                      </button>
                    </div>
                  </div>

                  {(onboardingData || vendor?.onboardingCompleted) ? (
                    <div className="space-y-6">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2 text-green-700">
                            <Building2 className="w-5 h-5" />
                            <span className="font-semibold">
                              {onboardingData?.completedAt
                                ? t('settings.onboarding.completedOn', { date: new Date(onboardingData.completedAt).toLocaleDateString() })
                                : t('settings.onboarding.completed')}
                            </span>
                          </div>
                          <button
                            onClick={() => navigate('/onboarding')}
                            className="text-sm text-green-700 hover:text-green-800 font-medium underline"
                          >
                            {t('settings.onboarding.reconfigure')}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('settings.onboarding.emptyTitle')}</h3>
                      <p className="text-gray-600">{t('settings.onboarding.emptyDescription')}</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'emailTemplates' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('settings.emailTemplates.heading')}</h2>
                  <p className="text-gray-600 mb-6">
                    {t('settings.emailTemplates.intro')}
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <p className="text-sm text-blue-800">
                      <strong>{t('settings.emailTemplates.noteLabel')}</strong> {t('settings.emailTemplates.note')}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate('/settings/email-templates')}
                    className="flex items-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    <span>{t('settings.emailTemplates.manage')}</span>
                  </button>
                </div>
              )}

              {activeTab === 'toneOfVoice' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('settings.toneOfVoice.heading')}</h2>
                  <p className="text-gray-600 mb-6">{t('settings.toneOfVoice.intro')}</p>

                  <form onSubmit={handleSubmit(onSubmit, onInvalidSubmit)}>
                    <div className="space-y-6">
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                        <p className="text-sm text-purple-800">
                          <strong>{t('settings.toneOfVoice.howItWorksLabel')}</strong> {t('settings.toneOfVoice.howItWorks')}
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-purple-500" />
                          {t('settings.toneOfVoice.toneDescription')}
                        </label>
                        <textarea
                          {...register('toneOfVoice' as any)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[160px]"
                          placeholder={t('settings.toneOfVoice.tonePlaceholder')}
                          rows={6}
                        />
                        <p className="mt-2 text-xs text-gray-500">{t('settings.toneOfVoice.maxChars')}</p>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">{t('settings.toneOfVoice.examplesHeading')}</h4>
                        <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                          <li>{t('settings.toneOfVoice.example1')}</li>
                          <li>{t('settings.toneOfVoice.example2')}</li>
                          <li>{t('settings.toneOfVoice.example3')}</li>
                        </ul>
                      </div>
                    </div>

                    <div className="mt-6 flex justify-end">
                      <button
                        type="submit"
                        disabled={!hasChanges || !isFormValid || isUpdating}
                        className="flex items-center space-x-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isUpdating ? (
                          <>
                            <Loader className="w-4 h-4 animate-spin" />
                            <span>{t('common.saving')}</span>
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4" />
                            <span>{t('common.saveChanges')}</span>
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}
              {activeTab === 'promoterActions' && <PromoterActionsSettings />}

              {activeTab === 'permissions' && <PermissionsSettings />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
